import { useContext, useEffect, useState, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useEntity } from '@backstage/plugin-catalog-react';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';
import RoleTile from './RoleTile';
import RoleByRoleTile from './RoleByRoleTile';
import {
  DevHubAlert,
  DevHubAvatar,
  DevHubBox,
  DevHubButton,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubListItemText,
  DevHubPaper,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { Progress } from '@backstage/core-components';
import {
  AppRole,
  Responsibility,
  RoleId,
  RolesQueryVariables,
  UserRole,
  UserRoleInfo,
  ResponsibleParty,
} from '../types/RoleTypes';
import { ModifyResponsibility } from './ModifyResponsibility';
import RoleFilter from './RoleFilter';
import { FunctionalAreaUser, Permission } from '../types/InsightsTypes';
import { SystemContext } from '../../../contexts/SystemContext';
import EditableApp360Field from '../EditableApp360Field';
import { getPartyName } from '../utils/partyUtils';

interface RoleByRole {
  roleName: string;
  roleDescription?: string;
  roleId?: number;
  parties: Array<{
    party: ResponsibleParty;
    responsibilityId: number;
  }>;
}

const GetRolesQuery = gql`
  query GetAppRolesAndAccessPolicy(
    $applicationId: Int!
    $filter: AuthorizationPolicyFilter!
  ) {
    resources {
      application(applicationId: $applicationId) {
        roles {
          id
          name
          displayName
          description
          responsibilities {
            responsibilityId
            roleName
            responsibleParty {
              ... on User {
                firstName
                lastName
                login
                email
              }
              ... on Group {
                name
                email
              }
            }
          }
        }
        authorizationPolicy(filter: $filter) {
          policy
          hasAccess
          data
        }
      }
    }
  }
`;

/**
 * Entry point into the roles and responsibilities, handles query and calls RoleDevHubCard
 */
export const ApplicationRoles = () => {
  const { entity } = useEntity();
  const { app, getPolicyAccess } = useContext(SystemContext);
  const [roles, setRoles] = useState<UserRole[]>([] as UserRole[]);
  const [edit, setEdit] = useState(false);
  const [searchResults, setSearchResults] = useState<UserRole[] | undefined>(
    undefined,
  );
  const [selectedRoles, setSelectedRoles] = useState([] as string[]);
  const [showDeleteIcon, setShowDeleteIcon] = useState(false);
  const [showDisabled, setShowDisabled] = useState(false);
  const storedViewByRole = localStorage.getItem('rolesViewByRole');
  const [viewByRole, setViewByRole] = useState(
    storedViewByRole === 'true' ? true : false,
  ); // false = by party (default), true = by role

  // Persist view mode preference to localStorage
  useEffect(() => {
    localStorage.setItem('rolesViewByRole', viewByRole.toString());
  }, [viewByRole]);

  // Memoize feature flag check to avoid repeated calls
  const isDboManagementEnabled = useMemo(
    () => ConfigHelper.instance.isFeatureEnabled(Feature.DBO_MANAGEMENT),
    [],
  );

  const isAppRetired = (): boolean => {
    return app?.status === 'Retired';
  };

  // Toggle the delete icon and disabling of uneditable roles when the "edit" button is clicked
  const handleEditClick = () => {
    setShowDeleteIcon(!showDeleteIcon);
    setEdit(prevEdit => !prevEdit);
    setShowDisabled(!showDisabled);
  };

  const appId: number = Number(
    entity?.metadata?.annotations?.['comcast.net/itrc-id'],
  );

  const rolesVariables: RolesQueryVariables = {
    applicationId: appId,
    filter: {
      policies: ['ManageRolesPolicy'],
    },
  };

  const { loading, error, data } = useQuery(GetRolesQuery, {
    errorPolicy: 'all',
    variables: rolesVariables,
  });

  const functionalAreaRoles = useMemo((): UserRole[] => {
    const functionalArea = app?.functionalAreaConnection[0];
    const areaRoles: UserRole[] = [];

    // Only include data leads from functional area if DBO_MANAGEMENT feature is disabled
    if (!isDboManagementEnabled) {
      functionalArea?.dataLeads?.forEach((dataLead: FunctionalAreaUser) => {
        const dataLeadRole = {
          party: JSON.stringify({
            __typename: 'User',
            firstName: dataLead?.firstName,
            lastName: dataLead?.lastName,
            login: dataLead?.login?.toLowerCase(),
            email: dataLead?.email,
          }),
          roles: [
            {
              displayName: 'Data Lead',
              responsibilityId: RoleId.FunctionalAreaRole,
              roleId: RoleId.FunctionalAreaRole,
              description:
                'As a part of the CPP program the Data Governance team has requested a Data Lead role. DATA SPOC',
            },
          ],
        };
        areaRoles.push(dataLeadRole);
      });
    }
    const dataBusinessOwner = {
      party: JSON.stringify({
        __typename: 'User',
        firstName: functionalArea?.dataBusinessOwner?.firstName,
        lastName: functionalArea?.dataBusinessOwner?.lastName,
        login: functionalArea?.dataBusinessOwner?.login?.toLowerCase(),
        email: functionalArea?.dataBusinessOwner?.email,
      }),
      roles: [
        {
          displayName: 'Data Business Owner',
          responsibilityId: RoleId.FunctionalAreaRole,
          roleId: RoleId.FunctionalAreaRole,
          description:
            'Role created in Support of the CPP initiative and intended to define the person notes as the "Data Business Owner"',
        },
      ],
    };
    areaRoles.push(dataBusinessOwner);

    return areaRoles;
  }, [app?.functionalAreaConnection, isDboManagementEnabled]);

  const filteredRoles = useMemo((): UserRole[] | undefined => {
    // Start with a copy of the roles array
    const mergedRoles: UserRole[] = [...roles];

    // Get functional area roles and merge them by party
    const areaRoles = functionalAreaRoles;
    areaRoles.forEach((functionalAreaRole: UserRole) => {
      const existingEntry = mergedRoles.find(
        entry => entry.party === functionalAreaRole.party,
      );
      if (existingEntry) {
        // Merge roles for existing party - but check for duplicates first
        functionalAreaRole.roles.forEach(newRole => {
          const roleExists = existingEntry.roles.some(
            existingRole => existingRole.displayName === newRole.displayName,
          );
          if (!roleExists) {
            existingEntry.roles.push(newRole);
          }
        });
      } else {
        // Add new party
        mergedRoles.push(functionalAreaRole);
      }
    });

    // Sort roles within each party alphabetically by displayName
    mergedRoles.forEach(entry => {
      entry.roles.sort((a, b) => a.displayName.localeCompare(b.displayName));
    });

    // Sort roles so that Data Business Owner roles appear first
    mergedRoles.sort((a, b) => {
      const aHasDataBusinessOwner = a.roles.some(
        role => role.displayName === 'Data Business Owner',
      );
      const bHasDataBusinessOwner = b.roles.some(
        role => role.displayName === 'Data Business Owner',
      );

      if (aHasDataBusinessOwner && !bHasDataBusinessOwner) {
        return -1; // a comes first
      }
      if (!aHasDataBusinessOwner && bHasDataBusinessOwner) {
        return 1; // b comes first
      }
      return 0; // maintain original order for equal cases
    });

    let result: UserRole[] | undefined =
      searchResults?.length !== undefined ? searchResults : mergedRoles;

    if (selectedRoles.length > 0) {
      result = result?.filter((role: UserRole) => {
        return role.roles.some((r: UserRoleInfo) =>
          selectedRoles.includes(r.displayName),
        );
      });
    }

    return result;
  }, [roles, searchResults, selectedRoles, functionalAreaRoles]);

  const handleRolesChange = (role: string) => {
    const isSelected = selectedRoles?.includes(role);

    if (isSelected) {
      setSelectedRoles(selectedRoles?.filter(e => e !== role));
    } else {
      setSelectedRoles([...selectedRoles, role]);
    }
  };

  const rolesByRole = useMemo((): RoleByRole[] => {
    const userRoles = filteredRoles;
    if (!userRoles) return [];

    const roleMap = new Map<string, RoleByRole>();

    userRoles.forEach((userRole: UserRole) => {
      const party = JSON.parse(userRole.party);

      userRole.roles.forEach((roleInfo: UserRoleInfo) => {
        const existingRole = roleMap.get(roleInfo.displayName);

        if (existingRole) {
          existingRole.parties.push({
            party,
            responsibilityId: roleInfo.responsibilityId,
          });
        } else {
          roleMap.set(roleInfo.displayName, {
            roleName: roleInfo.displayName,
            roleDescription: roleInfo.description,
            roleId: roleInfo.roleId,
            parties: [
              {
                party,
                responsibilityId: roleInfo.responsibilityId,
              },
            ],
          });
        }
      });
    });

    // Convert map to array and sort alphabetically by role name
    const sortedRoles = Array.from(roleMap.values()).sort((a, b) =>
      a.roleName.localeCompare(b.roleName),
    );

    // Sort parties within each role alphabetically by name
    sortedRoles.forEach(role => {
      role.parties.sort((a, b) => {
        const aName = getPartyName(a.party);
        const bName = getPartyName(b.party);
        return aName.localeCompare(bName);
      });
    });

    return sortedRoles;
  }, [filteredRoles]);

  const canEditInfo = (): boolean => {
    return getPolicyAccess('UpdateApplicationPolicy') && !isAppRetired();
  };

  const canEditRoles = (roleId: number | undefined): boolean => {
    if (isAppRetired()) {
      return false;
    }

    let editable = false;

    // If roleId is undefined, check if the user can edit any roles at all
    if (roleId === undefined) {
      // Check if user has at least one editable role
      const editableRole =
        data?.resources?.application?.authorizationPolicy?.some(
          (permission: Permission) =>
            permission?.policy === 'ManageRolesPolicy' &&
            permission?.hasAccess === true,
        );

      editable = editableRole;
    } else {
      const findRole = data?.resources?.application?.authorizationPolicy?.find(
        (permission: Permission) =>
          permission?.policy === 'ManageRolesPolicy' &&
          permission?.data?.roleId === roleId,
      );

      editable = findRole?.hasAccess ?? false;
    }

    return editable;
  };

  useEffect(() => {
    if (data?.resources?.application?.roles?.length > 0) {
      // Filter out Owner and Data Business Owner roles
      // Only filter out Data Lead roles if DBO_MANAGEMENT feature is disabled
      const getRoles = data?.resources?.application?.roles.filter(
        (role: AppRole) => {
          if (
            role.id === RoleId.Owner ||
            role.id === RoleId.DataBusinessOwner
          ) {
            return false;
          }
          // Include Data Lead roles if DBO_MANAGEMENT feature is enabled
          if (role.id === RoleId.DataLead) {
            return isDboManagementEnabled;
          }
          return true;
        },
      );
      getRoles?.sort((a: { displayName: string }, b: { displayName: any }) =>
        a.displayName.localeCompare(b.displayName),
      );

      const combinedRoles: UserRole[] = [];

      getRoles?.forEach((obj: AppRole) => {
        obj?.responsibilities?.forEach((responsibility: Responsibility) => {
          const responsibleParty = responsibility.responsibleParty;
          // Normalize login to lowercase for users to ensure proper party comparison
          const normalizedParty =
            responsibleParty && 'login' in responsibleParty
              ? {
                  ...responsibleParty,
                  login: responsibleParty.login?.toLowerCase(),
                }
              : responsibleParty;
          const party = JSON.stringify(normalizedParty);
          const displayName = obj.displayName;
          const description = obj.description;
          const responsibilityId = responsibility.responsibilityId;
          const roleId = obj.id;

          const existingEntry = combinedRoles.find(
            entry => entry.party === party,
          );
          if (existingEntry) {
            existingEntry.roles.push({
              displayName,
              responsibilityId,
              roleId,
              description,
            });
          } else {
            combinedRoles.push({
              party,
              roles: [{ displayName, responsibilityId, roleId, description }],
            });
          }
        });
      });

      combinedRoles.forEach(entry => entry?.roles?.sort());
      setRoles(combinedRoles);
    }
  }, [data, isDboManagementEnabled]);

  if (loading) {
    return <Progress />;
  } else if (error) {
    return (
      <DevHubAlert severity="error">Error loading application data</DevHubAlert>
    );
  }

  return (
    <>
      {isAppRetired() && (
        <DevHubBox mb={2}>
          <DevHubAlert severity="info">
            This application is retired and roles cannot be modified.
          </DevHubAlert>
        </DevHubBox>
      )}
      <DevHubGrid container alignItems="center" justifyContent="flex-end">
        <DevHubGrid item xs={6} md={8} lg={8}>
          <DevHubTypography variant="h2">Find Roles</DevHubTypography>
        </DevHubGrid>
        <DevHubGrid item xs={3} md={2} lg={2}>
          <ModifyResponsibility
            appId={appId}
            rolesQuery={GetRolesQuery}
            rolesVariables={rolesVariables}
            rolesSearch
            buttonText="Add Role"
            canEditRoles={canEditRoles}
          />
        </DevHubGrid>
        <DevHubGrid item xs={3} md={2} lg={2}>
          <DevHubButton
            variant="contained"
            color="primary"
            size="small"
            height="large"
            fullWidth
            onClick={handleEditClick}
            startIcon={<DevHubDynamicIcon iconName="Edit" />}
            disabled={!canEditRoles(undefined)}
          >
            {edit ? 'Stop Editing' : 'Edit'}
          </DevHubButton>
        </DevHubGrid>
      </DevHubGrid>
      <RoleFilter
        setSearchResults={setSearchResults}
        searchResults={searchResults}
        roleList={roles}
        handleRoleChange={handleRolesChange}
        selectedRoles={selectedRoles}
        viewByRole={viewByRole}
        onViewModeChange={setViewByRole}
      />
      <DevHubPaper>
        <DevHubTypography paragraph variant="h6">
          Application Owner
        </DevHubTypography>
        <EditableApp360Field
          applicationId={app?.applicationId}
          editField="ownerLogin"
          editComponent="userSelect"
          edit={edit}
          hasEditPermission={canEditInfo()}
        >
          <DevHubGrid
            container
            padding="xsmall"
            alignItems="center"
            justifyContent="flex-start"
          >
            <DevHubGrid item>
              <DevHubAvatar>
                <DevHubTypography fontColor="white">
                  {app?.owner?.firstName === null
                    ? 'N/A'
                    : app?.owner?.firstName?.charAt(0) +
                      app?.owner?.lastName?.charAt(0)}
                </DevHubTypography>
              </DevHubAvatar>
            </DevHubGrid>
            <DevHubGrid item>
              <DevHubListItemText
                primary={
                  app?.owner?.firstName === null
                    ? 'No Owner Assigned'
                    : `${app?.owner?.firstName} ${app?.owner?.lastName}`
                }
                secondary={<div>{app?.owner?.email}</div>}
              />
            </DevHubGrid>
          </DevHubGrid>
        </EditableApp360Field>
      </DevHubPaper>
      <DevHubBox>
        <DevHubGrid container key={viewByRole ? 'by-role' : 'by-party'}>
          {viewByRole
            ? // View by Role - Card View: Show roles with their assigned parties
              rolesByRole.map(roleGroup => {
                return (
                  <DevHubGrid
                    item
                    xs={12}
                    md={6}
                    lg={4}
                    key={roleGroup.roleId ?? roleGroup.roleName}
                  >
                    <RoleByRoleTile
                      appId={appId}
                      roleName={roleGroup.roleName}
                      roleDescription={roleGroup.roleDescription}
                      roleId={roleGroup.roleId}
                      parties={roleGroup.parties}
                      rolesQuery={GetRolesQuery}
                      rolesVariables={rolesVariables}
                      showDeleteIcon={showDeleteIcon}
                      showDisabled={showDisabled}
                      canEditRoles={canEditRoles}
                    />
                  </DevHubGrid>
                );
              })
            : // View by Party: Show parties with their assigned roles
              filteredRoles?.map((role, index) => {
                if (!role || Object.keys(role).length === 0) return null;
                const partyRole = JSON.parse(role.party);
                const partyKey =
                  partyRole.__typename === 'User'
                    ? partyRole.login || `user-${index}`
                    : partyRole.name || `group-${index}`;
                return (
                  <DevHubGrid item xs={12} md={6} lg={4} key={partyKey}>
                    <RoleTile
                      rolesQuery={GetRolesQuery}
                      rolesVariables={rolesVariables}
                      appId={appId}
                      party={partyRole}
                      roles={role.roles}
                      showDeleteIcon={showDeleteIcon}
                      showDisabled={showDisabled}
                      canEditRoles={canEditRoles}
                    />
                  </DevHubGrid>
                );
              })}
        </DevHubGrid>
      </DevHubBox>
    </>
  );
};
